import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import NotFoundPage from './NotFoundPage';
import { useAuthGuard } from '../hooks/useAuthGuard';
import './SchoolPage.css';

interface SchoolData {
  schoolName?: string;
  email?: string;
  phoneNumber?: string;
  address?: {
    postal_code?: string;
    state?: string;
    city?: string;
    town?: string;
    line1?: string;
  };
  latitude?: number;
  longitude?: number;
  stripeAccountInfo?: {
    businessUrl?: string;
  };
}

// 20種類のグラデーションパレット
const GRADIENT_PALETTES = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
  'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  'linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%)',
  'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
  'linear-gradient(135deg, #ff8a80 0%, #ea6100 100%)',
  'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
  'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
  'linear-gradient(135deg, #ff6e7f 0%, #bfe9ff 100%)',
  'linear-gradient(135deg, #c471f5 0%, #fa71cd 100%)',
  'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
  'linear-gradient(135deg, #fad0c4 0%, #ffd1ff 100%)',
  'linear-gradient(135deg, #ff9a56 0%, #ff6a88 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
];

// 各パレットが暗い色かどうかのフラグ（暗い場合はtrue）
const IS_DARK_PALETTE = [
  true,  // #667eea 0%, #764ba2 100% - 暗めの紫
  false, // #f093fb 0%, #f5576c 100% - 明るいピンク/赤
  false, // #4facfe 0%, #00f2fe 100% - 明るい青
  false, // #43e97b 0%, #38f9d7 100% - 明るい緑/シアン
  false, // #fa709a 0%, #fee140 100% - 明るいピンク/黄
  true,  // #30cfd0 0%, #330867 100% - 暗い紫
  false, // #a8edea 0%, #fed6e3 100% - 明るい水色/ピンク
  false, // #ff9a9e 0%, #fecfef 50%, #fecfef 100% - 明るいピンク
  false, // #ffecd2 0%, #fcb69f 100% - 明るいオレンジ
  true,  // #ff8a80 0%, #ea6100 100% - 暗いオレンジ
  false, // #84fab0 0%, #8fd3f4 100% - 明るい緑/青
  false, // #a1c4fd 0%, #c2e9fb 100% - 明るい青
  false, // #ff6e7f 0%, #bfe9ff 100% - 明るいピンク/青
  false, // #c471f5 0%, #fa71cd 100% - 明るい紫/ピンク
  false, // #fbc2eb 0%, #a6c1ee 100% - 明るいピンク/青
  false, // #fad0c4 0%, #ffd1ff 100% - 明るいピンク
  false, // #ff9a56 0%, #ff6a88 100% - 明るいオレンジ/ピンク
  false, // #4facfe 0%, #00f2fe 100% - 明るい青（重複）
  false, // #43e97b 0%, #38f9d7 100% - 明るい緑/シアン（重複）
  false, // #fa709a 0%, #fee140 100% - 明るいピンク/黄（重複）
];

// schoolIdをハッシュ化してパレットインデックスを取得
function getGradientIndex(schoolId: string): number {
  let hash = 0;
  for (let i = 0; i < schoolId.length; i++) {
    const char = schoolId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash) % GRADIENT_PALETTES.length;
}

export default function SchoolPage() {
  useAuthGuard();
  const { schoolId } = useParams<{ schoolId: string }>();
  const [schoolData, setSchoolData] = useState<SchoolData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // schoolIdに基づいてグラデーションカラーとテキスト色を決定（Hooksは条件分岐の前に呼ぶ必要がある）
  const gradientStyle = useMemo(() => {
    if (!schoolId) return { background: '', textColor: '#333' };
    const gradientIndex = getGradientIndex(schoolId);
    const isDark = IS_DARK_PALETTE[gradientIndex] || false;
    return {
      background: GRADIENT_PALETTES[gradientIndex],
      textColor: isDark ? '#fff' : '#333',
    };
  }, [schoolId]);

  useEffect(() => {
    const fetchSchoolData = async () => {
      if (!schoolId) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/schools/${encodeURIComponent(schoolId)}`);
        if (response.status === 404) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        if (!response.ok) {
          throw new Error(`Failed to fetch school data (${response.status})`);
        }

        const payload = await response.json();
        if (!payload?.data) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        setSchoolData(payload.data as SchoolData);
      } catch (error) {
        console.error('Error fetching school data:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchSchoolData();
  }, [schoolId]);

  if (loading) {
    return (
      <div className="school-page">
        <div className="loading-container">
          <p>読み込み中...</p>
        </div>
      </div>
    );
  }

  if (notFound || !schoolData) {
    return <NotFoundPage />;
  }

  // 住所を組み立て（番地まで含める）
  const fullAddress = schoolData.address
    ? [
        schoolData.address.postal_code,
        schoolData.address.state,
        schoolData.address.city,
        schoolData.address.town,
        schoolData.address.line1
      ]
        .filter(Boolean)
        .join(' ')
        .trim()
    : '';

  const mapUrl = schoolData.latitude && schoolData.longitude
    ? `https://www.google.com/maps?q=${schoolData.latitude},${schoolData.longitude}`
    : fullAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`
    : null;

  return (
    <div className="school-page">
      <div className="school-hero" style={{ background: gradientStyle.background }}>
        <div className="container">
          <h1 className="school-name" style={{ color: gradientStyle.textColor }}>
            {schoolData.schoolName || '教室名未設定'}
          </h1>
        </div>
      </div>

      <main className="school-main">
        <div className="container">
          <section className="school-section">
            <h2 className="section-title">連絡先</h2>
            <div className="contact-info">
              {schoolData.phoneNumber && (
                <div className="contact-item">
                  <span className="contact-label">電話番号</span>
                  <span className="contact-value">
                    <a href={`tel:${schoolData.phoneNumber}`}>{schoolData.phoneNumber}</a>
                  </span>
                </div>
              )}
              {schoolData.email && (
                <div className="contact-item">
                  <span className="contact-label">メールアドレス</span>
                  <span className="contact-value">
                    <a href={`mailto:${schoolData.email}`}>{schoolData.email}</a>
                  </span>
                </div>
              )}
              {fullAddress && (
                <div className="contact-item">
                  <span className="contact-label">住所</span>
                  <span className="contact-value">
                    {schoolData.address?.postal_code && (
                      <span>〒{schoolData.address.postal_code} </span>
                    )}
                    {[
                      schoolData.address?.state,
                      schoolData.address?.city,
                      schoolData.address?.town,
                      schoolData.address?.line1
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  </span>
                </div>
              )}
            </div>
          </section>

          {mapUrl && (
            <section className="school-section">
              <h2 className="section-title">アクセス</h2>
              <div className="map-container">
                {import.meta.env.VITE_GOOGLE_MAPS_API_KEY ? (
                  <>
                  <iframe
                      src={
                        schoolData.latitude && schoolData.longitude
                          ? `https://www.google.com/maps/embed/v1/search?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&q=${schoolData.latitude},${schoolData.longitude}`
                          : `https://www.google.com/maps/embed/v1/search?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(fullAddress)}`
                      }
                    width="100%"
                    height="450"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                    <div className="map-note">
                      <p>※ 地図が表示されない場合は、下のリンクからGoogleマップで確認できます。</p>
                    </div>
                  </>
                ) : (
                  <div className="map-placeholder">
                    <p>地図を表示するにはGoogle Maps APIキーが必要です。下のリンクからGoogleマップで確認できます。</p>
                  </div>
                )}
                <div className="map-link">
                  <a href={mapUrl} target="_blank" rel="noopener noreferrer">
                    Googleマップで開く
                  </a>
                </div>
              </div>
            </section>
          )}
        </div>
      </main>

      <footer className="school-footer">
        <div className="container">
          <p>
            &copy; 2024 LessonMate {schoolData.schoolName ? `・ ${schoolData.schoolName}` : ''}
          </p>
        </div>
      </footer>
    </div>
  );
}

